// Read WoW combat logs straight off the user's disk via the File System Access
// API (Chromium). The user picks their WoW folder once; we persist the directory
// HANDLE in IndexedDB (handles are structured-cloneable — localStorage only holds
// strings, so it can't store one) and re-use it on later visits. Nothing is ever
// uploaded: the logs are read locally in the browser and parsed in the worker.

// Permission methods are part of the File System Access spec but not yet in
// lib.dom, so augment the handle type here.
type PermMode = { mode?: "read" | "readwrite" };
interface PermissionedHandle {
  queryPermission(descriptor?: PermMode): Promise<PermissionState>;
  requestPermission(descriptor?: PermMode): Promise<PermissionState>;
}
type DirHandle = FileSystemDirectoryHandle & PermissionedHandle;

// lib.dom (this TS version) ships FileSystemDirectoryHandle but not these bits.
declare global {
  interface Window {
    showDirectoryPicker(options?: {
      id?: string;
      mode?: "read" | "readwrite";
      startIn?: FileSystemHandle | string;
    }): Promise<FileSystemDirectoryHandle>;
  }
  interface FileSystemDirectoryHandle {
    values(): AsyncIterableIterator<FileSystemHandle>;
  }
}

export interface CombatLogFile {
  name: string;
  handle: FileSystemFileHandle;
  lastModified: number;
  size: number;
}

export function supportsFileSystemAccess(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

// --- persistence: one directory handle in IndexedDB --------------------------
const DB_NAME = "raidlogger";
const STORE = "handles";
const KEY = "wow-dir";

function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
      req.transaction!.oncomplete = () => db.close();
    };
  });
}

async function saveHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  await withStore("readwrite", (s) => s.put(handle, KEY));
}
async function loadHandle(): Promise<DirHandle | null> {
  return (await withStore<DirHandle | undefined>("readonly", (s) => s.get(KEY))) ?? null;
}
export async function forgetWowDirectory(): Promise<void> {
  await withStore("readwrite", (s) => s.delete(KEY));
}

// --- permission --------------------------------------------------------------
// queryPermission is callable anytime; requestPermission needs a user gesture,
// so only pass {request:true} from a click handler.
export async function verifyReadPermission(
  handle: FileSystemDirectoryHandle,
  request: boolean,
): Promise<boolean> {
  const h = handle as DirHandle;
  if ((await h.queryPermission({ mode: "read" })) === "granted") return true;
  if (request && (await h.requestPermission({ mode: "read" })) === "granted") return true;
  return false;
}

// --- restoring + drag-drop ---------------------------------------------------
/** The previously-saved directory handle, if any. The caller checks permission
 *  (verifyReadPermission) to decide between auto-loading and prompting. */
export async function restoreWowDirectory(): Promise<FileSystemDirectoryHandle | null> {
  return loadHandle();
}

/** Pull a directory handle out of a drop event and persist it. Drag-and-drop is
 *  NOT subject to the picker's blocklist, so this is the way to get a handle for
 *  folders Chrome refuses to open via showDirectoryPicker — notably anything
 *  under Program Files (the default WoW install). Returns null if what was
 *  dropped wasn't a folder. */
export async function directoryFromDrop(e: DragEvent): Promise<FileSystemDirectoryHandle | null> {
  const item = e.dataTransfer?.items?.[0] as
    | (DataTransferItem & { getAsFileSystemHandle?(): Promise<FileSystemHandle | null> })
    | undefined;
  if (!item || item.kind !== "file" || !item.getAsFileSystemHandle) return null;
  const handle = await item.getAsFileSystemHandle();
  if (!handle || handle.kind !== "directory") return null;
  await saveHandle(handle as FileSystemDirectoryHandle);
  return handle as FileSystemDirectoryHandle;
}

// --- finding the combat logs -------------------------------------------------
// The user may pick the WoW root, a flavour folder, or the Logs folder itself,
// so look in the obvious spots rather than walking the whole (huge) install.
const LOG_RE = /^WoWCombatLog.*\.txt$/i;
const CANDIDATE_PATHS: string[][] = [
  [],
  ["Logs"],
  ["_retail_", "Logs"],
  ["_classic_", "Logs"],
  ["_classic_era_", "Logs"],
  ["_ptr_", "Logs"],
];

async function directoryAt(root: FileSystemDirectoryHandle, parts: string[]): Promise<FileSystemDirectoryHandle | null> {
  let dir = root;
  for (const part of parts) {
    try {
      dir = await dir.getDirectoryHandle(part);
    } catch {
      return null;
    }
  }
  return dir;
}

/** All WoWCombatLog*.txt files found under the known sub-paths, newest first. */
export async function listCombatLogs(root: FileSystemDirectoryHandle): Promise<CombatLogFile[]> {
  const byName = new Map<string, CombatLogFile>();
  for (const parts of CANDIDATE_PATHS) {
    const dir = await directoryAt(root, parts);
    if (!dir) continue;
    for await (const entry of dir.values()) {
      if (entry.kind !== "file" || !LOG_RE.test(entry.name)) continue;
      if (byName.has(entry.name)) continue;
      const file = await (entry as FileSystemFileHandle).getFile();
      byName.set(entry.name, {
        name: entry.name,
        handle: entry as FileSystemFileHandle,
        lastModified: file.lastModified,
        size: file.size,
      });
    }
  }
  return [...byName.values()].sort((a, b) => b.lastModified - a.lastModified);
}

export async function readCombatLog(file: CombatLogFile): Promise<string> {
  return (await file.handle.getFile()).text();
}
